import { useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  FormLabel,
  Group,
  IconCrossCircle,
  Input,
  Select,
  Textarea,
  Typography,
  Wrapper
} from '@screentone/core';

import type { ModuleParameterSchemaEntry } from './ModuleParameterSettings';

const PARAMETER_TYPES = ['string', 'boolean', 'enum'] as const;

interface Props {
  initial?: ModuleParameterSchemaEntry;
  onSave: (entry: ModuleParameterSchemaEntry) => void;
  onCancel: () => void;
}

export const ModuleParameterSchemaForm = ({ initial, onSave, onCancel }: Props) => {
  const [key, setKey] = useState(initial?.key ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [type, setType] = useState<string>(initial?.type ?? 'string');
  const [enumValuesRaw, setEnumValuesRaw] = useState((initial?.enumValues ?? []).join(', '));
  const [deprecated, setDeprecated] = useState(initial?.deprecated ?? false);

  const isEditing = !!initial;

  const isValidKey = !key.trim() || /^[a-z][a-z0-9_]*$/.test(key.trim());
  const isSaveDisabled = !key.trim() || !isValidKey || !label.trim() || (type === 'enum' && !enumValuesRaw.trim());

  const handleSave = () => {
    const trimmedKey = key.trim();
    const trimmedLabel = label.trim();
    if (!trimmedKey || !trimmedLabel || !isValidKey) return;

    const enumValues =
      type === 'enum'
        ? enumValuesRaw
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : undefined;

    onSave({ key: trimmedKey, label: trimmedLabel, type, enumValues, deprecated: deprecated || undefined });
  };

  return (
    <Wrapper>
      <Typography variant="header4" margin={{ bottom: 'sm' }}>
        {isEditing ? 'Edit Parameter' : 'New Parameter'}
      </Typography>
      <Group direction="column" gap="sm">
        <FormLabel label="Key" fullWidth>
          <Group gap="xs" wrap={false} valign="center">
            <Input
              value={key}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKey(e.target.value)}
              placeholder="e.g. summary_priority"
              disabled={isEditing}
              error={!isValidKey}
            />
            {!isEditing && key && <Button tertiary icon={IconCrossCircle} onClick={() => setKey('')} />}
          </Group>
          {!isValidKey && (
            <Typography variant="note" color="lava">
              Key must be snake_case: lowercase letters, digits, and underscores only (e.g. summary_priority)
            </Typography>
          )}
        </FormLabel>
        <FormLabel label="Label" fullWidth>
          <Group gap="xs" wrap={false} valign="center">
            <Input
              value={label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
              placeholder="e.g. Summary Priority"
            />
            {label && <Button tertiary icon={IconCrossCircle} onClick={() => setLabel('')} />}
          </Group>
        </FormLabel>
        <FormLabel label="Type" fullWidth>
          <Select value={type} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setType(e.target.value)}>
            {PARAMETER_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </FormLabel>
        {type === 'enum' && (
          <FormLabel label="Enum Values (comma-separated)" fullWidth>
            <Group gap="xs" wrap={false} valign="center">
              <Textarea
                value={enumValuesRaw}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEnumValuesRaw(e.target.value)}
                placeholder="e.g. HIGH, MEDIUM, LOW"
                rows={2}
              />
              {enumValuesRaw && <Button tertiary icon={IconCrossCircle} onClick={() => setEnumValuesRaw('')} />}
            </Group>
          </FormLabel>
        )}
        {isEditing && (
          <FormLabel label="Deprecated" labelPosition="right" fullWidth>
            <Checkbox
              checked={deprecated}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeprecated(e.target.checked)}
            />
          </FormLabel>
        )}
      </Group>
      <Divider margin={{ top: 'md', bottom: 'md' }} />
      <Group gap="sm">
        <Button secondary onClick={onCancel} margin={{ right: 'sm' }}>
          Cancel
        </Button>
        <Button primary onClick={handleSave} disabled={isSaveDisabled}>
          {isEditing ? 'Update' : 'Create'}
        </Button>
      </Group>
    </Wrapper>
  );
};
